/**
 * productionRecordService.ts
 * API service for fetching production records (ICS + HTE) from inventory-service.
 */
import api from './api';

export interface ProductionRecord {
  production_number: string;
  starting_date: string;
  ending_date: string;
  operator_id: string;
  memo: string;
  is_approved: string;
  location_code: string;
  source: 'ICS' | 'HTE';
  sync_status: string | null;
  session_number: string | null;
  detail_count: number;
  dominant_p_type: string | null;
  reference_numbers: string[] | null;
  created_by_name: string | null;
}

export interface WeightEntry {
  id: string;
  weight: string;
}

export interface ProductionRecordDetail {
  id: number;
  production_number: string;
  p_type: string;
  item_number: string;
  quantity: number;
  rate: number;
  use_sub_unit: string;
  is_frozen: string;
  quantity_shipped: number;
  m_date: string;
  m_time: string;
  reference_number: string;
  date_code: string;
  remark: string;
  warehouse_code: string;
  location_code: string;
  material_doc_no: string | null;
  material_doc_type: string | null;
  // Output-specific fields (populated when p_type === 'O')
  qs_type: string | null;
  duration: number | string | null;
  receiving_scan_complete: string | null;
  pending_for_qs_allocation: string | null;
  qs_allocation_record_number: string | null;
  e_item: string | null;
  salesperson_number_qs1: string | null;
  item_english_name: string;
  item_chinese_name: string;
  item_unit: string;
  item_sub_unit: string;
  unit: string;
  // Inventory metadata used by the Amount formula (mirrors VB6 ProductionRecord.frm)
  item_type: string | null;
  priced_by_fixed_sub_unit: string | null;
  item_sub_quantity: number | string | null;
  weight_count: number;
  total_weight: number;
  weights: WeightEntry[];
}

export interface RecordsResponse {
  records: ProductionRecord[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    total_pages: number;
  };
}

export interface RecordDetailsResponse {
  summary: ProductionRecord & { session_number: string | null };
  details: ProductionRecordDetail[];
}

export interface ProductionRecordParams {
  page?: number;
  limit?: number;
  source?: string;
  search?: string;
  date_from?: string;
  date_to?: string;
  location_code?: string;
  sync_status?: string;
}

export const productionRecordService = {
  async getRecords(params?: ProductionRecordParams): Promise<RecordsResponse> {
    const response = await api.get('/inventory/production-records', { params });
    return response.data.data;
  },

  async getRecordDetails(productionNumber: string): Promise<RecordDetailsResponse> {
    const response = await api.get(`/inventory/production-records/${encodeURIComponent(productionNumber)}/details`);
    return response.data.data;
  }
};
